import { getSmartCollection } from 'api/product/getSmartCollection';
import useTest10173 from 'hooks/test/use-test-10173';
import { CollectionProduct } from 'models/collection/collection';
import { useMemo } from 'react';
import { useSavingPlanStore } from 'stores/saving-plan';
import { useRootStore } from 'stores/use-root-store';
import { isEmptyProfile } from 'stores/pet-profile-new/utils/is-empty-profile';
import { generateInputFromPetProfile } from 'utils/api/smartCollection';
import useSWR from 'swr';

interface Output {
  products: CollectionProduct[];
  totalCount: number;
  isLoading: boolean;
}

const RECOMMENDED_AMOUNT = 3;

/** Resolves the recommended cat best-sellers from the current pet profile, so every Picky Cats entry point shows and gates on the same products. */
const usePickyCatsProducts = (): Output => {
  const petProfile = useRootStore((store) => store.currentPetProfile);
  const activePlan = useSavingPlanStore((store) => store.activePlan);
  const { isPickyCatsEnabled, loading: isPickyCatsAssignmentLoading } = useTest10173();

  const isEmpty = isEmptyProfile(petProfile);

  const isPickyCatsAssignmentPending =
    isPickyCatsAssignmentLoading &&
    petProfile.petType === 'cat' &&
    Boolean(petProfile.selectedDislikedProteins?.length);
  const smartCollectionPayload = useMemo(
    () =>
      isEmpty || isPickyCatsAssignmentPending
        ? undefined
        : generateInputFromPetProfile({
            petProfile,
            includeDislikedProteins: isPickyCatsEnabled,
          }),
    [isEmpty, isPickyCatsAssignmentPending, isPickyCatsEnabled, petProfile]
  );

  const { data: collection, isValidating } = useSWR(
    smartCollectionPayload
      ? [smartCollectionPayload, activePlan?.ruleId, 'picky-cats-products']
      : null,
    ([payload]) => getSmartCollection(payload, activePlan)
  );

  const products = useMemo(
    () =>
      (collection?.items ?? [])
        .slice(0, RECOMMENDED_AMOUNT)
        .map((item) => new CollectionProduct(item)),
    [collection]
  );

  return {
    products,
    totalCount: collection?.items?.length ?? 0,
    isLoading: isValidating || isPickyCatsAssignmentPending,
  };
};

export default usePickyCatsProducts;
